import { useEffect, useRef, useState } from 'react';

const SCROLL_EPSILON = 1;

const useDataTableScrollDirections = () => {
  const tableScrollWrapperRef = useRef<HTMLDivElement>(null);
  const [scrollDirections, setScrollDirections] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  useEffect(() => {
    const wrapper = tableScrollWrapperRef.current;

    if (!wrapper) {
      return;
    }

    const scrollContainer = wrapper.querySelector<HTMLElement>('[data-slot="table-container"]');

    if (!scrollContainer) {
      return;
    }

    const updateScrollDirections = () => {
      const maxScrollLeft = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0);
      const canScrollLeft = scrollContainer.scrollLeft > SCROLL_EPSILON;
      const canScrollRight = scrollContainer.scrollLeft < maxScrollLeft - SCROLL_EPSILON;

      setScrollDirections((current) => {
        if (current.canScrollLeft === canScrollLeft && current.canScrollRight === canScrollRight) {
          return current;
        }

        return { canScrollLeft, canScrollRight };
      });
    };

    updateScrollDirections();

    scrollContainer.addEventListener('scroll', updateScrollDirections, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollDirections);
    resizeObserver.observe(scrollContainer);

    const tableElement = scrollContainer.querySelector<HTMLTableElement>('table');

    if (tableElement) {
      resizeObserver.observe(tableElement);
    }

    window.addEventListener('resize', updateScrollDirections);

    return () => {
      scrollContainer.removeEventListener('scroll', updateScrollDirections);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollDirections);
    };
  }, []);

  return {
    tableScrollWrapperRef,
    ...scrollDirections,
  };
};

export default useDataTableScrollDirections;
